import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { take } from 'rxjs';
import { BRANDING } from '../../../core/config/branding';

import { ConfirmComponent } from '../../../components/core/confirm/confirm.component';
import { ValidationErrorComponent } from '../../../components/core/validation-error/validation-error.component';
import {
  OrganizationCreateRequest,
  OrganizationCreateSuccess,
  OrganizationGenerateCredentialsFailure,
  OrganizationGenerateCredentialsRequest,
  OrganizationGenerateCredentialsSuccess,
  OrganizationListRequest,
  OrganizationSetActiveRequest,
  OrganizationUpdateRequest,
  OrganizationUpdateSuccess,
} from '../../../store/organization/organization.actions';
import type { Organization } from '../../../store/organization/organization.models';
import { selectAllOrganizations, selectOrganizationValidationErrors } from '../../../store/organization/organization.selectors';

type GeneratedCredentials = {
  organizationName: string;
  appKey: string;
  appSecret: string;
};

@Component({
  selector: 'app-super-admin-organizations',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    ValidationErrorComponent,
  ],
  templateUrl: './super-admin-organizations.html',
  styleUrl: './super-admin-organizations.scss',
})
export class SuperAdminOrganizations {
  protected readonly brand = BRANDING;
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);

  protected readonly organizations = signal<Organization[]>([]);
  protected readonly searchTerm = signal('');
  protected readonly editingOrganizationId = signal<string | null>(null);
  protected readonly isComposerOpen = signal(false);
  protected readonly validationErrors = signal<string[]>([]);
  protected readonly generatedCredentials = signal<GeneratedCredentials | null>(null);

  protected readonly organizationForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.required]],
    address_line_1: ['', [Validators.required]],
    address_line_2: [''],
    city: ['', [Validators.required]],
    state: ['', [Validators.required]],
    country: ['', [Validators.required]],
    zip: ['', [Validators.required]],
    is_active: [true],
  });

  constructor() {
    this.store.dispatch(OrganizationListRequest());

    this.store.select(selectAllOrganizations)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((organizations) => {
        this.organizations.set(organizations ?? []);
      });

    this.store.select(selectOrganizationValidationErrors)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((errors) => {
        this.validationErrors.set(Array.isArray(errors) ? errors as string[] : []);
      });

    this.actions$.pipe(ofType(OrganizationCreateSuccess, OrganizationUpdateSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closeComposer();
      });

    this.actions$.pipe(ofType(OrganizationGenerateCredentialsSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ id, appKey, appSecret }) => {
        const organization = this.organizations().find((item) => item.id === id);
        this.generatedCredentials.set({
          organizationName: organization?.name || 'Organization',
          appKey,
          appSecret,
        });
      });

    this.actions$.pipe(ofType(OrganizationGenerateCredentialsFailure), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.generatedCredentials.set(null);
      });
  }

  protected filteredOrganizations(): Organization[] {
    const query = this.searchTerm().trim().toLowerCase();
    if (!query) {
      return this.organizations();
    }

    return this.organizations().filter((organization) =>
      [organization.name, organization.email, organization.phone, organization.sid || '']
        .some((value) => value?.toLowerCase().includes(query)),
    );
  }

  protected updateSearchTerm(value: string): void {
    this.searchTerm.set(value);
  }

  protected openCreateComposer(): void {
    this.generatedCredentials.set(null);
    this.validationErrors.set([]);
    this.editingOrganizationId.set(null);
    this.organizationForm.reset({
      name: '',
      email: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      country: '',
      zip: '',
      is_active: true,
    });
    this.isComposerOpen.set(true);
  }

  protected openEditComposer(organization: Organization): void {
    this.generatedCredentials.set(null);
    this.validationErrors.set([]);
    this.editingOrganizationId.set(organization.id);
    this.organizationForm.reset({
      name: organization.name || '',
      email: organization.email || '',
      phone: organization.phone || '',
      address_line_1: organization.address?.address_line_1 || '',
      address_line_2: organization.address?.address_line_2 || '',
      city: organization.address?.city || '',
      state: organization.address?.state || '',
      country: organization.address?.country || '',
      zip: organization.address?.zip || '',
      is_active: organization.is_active,
    });
    this.isComposerOpen.set(true);
  }

  protected closeComposer(): void {
    this.isComposerOpen.set(false);
    this.editingOrganizationId.set(null);
    this.validationErrors.set([]);
  }

  protected saveOrganization(): void {
    this.validationErrors.set([]);
    if (this.organizationForm.invalid) {
      this.organizationForm.markAllAsTouched();
      return;
    }

    const value = this.organizationForm.getRawValue();
    const editingOrganizationId = this.editingOrganizationId();

    if (editingOrganizationId) {
      this.store.dispatch(OrganizationUpdateRequest({
        payload: {
          id: editingOrganizationId,
          organization: {
            name: value.name.trim(),
            email: value.email.trim(),
            phone: value.phone.trim(),
            address: {
              address_line_1: value.address_line_1.trim(),
              address_line_2: value.address_line_2.trim(),
              city: value.city.trim(),
              state: value.state.trim(),
              country: value.country.trim(),
              zip: value.zip.trim(),
            },
            config: {},
            membership: { is_member: true },
            is_active: !!value.is_active,
          },
        },
      }));
      return;
    }

    this.store.dispatch(OrganizationCreateRequest({
      payload: {
        name: value.name.trim(),
        email: value.email.trim(),
        phone: value.phone.trim(),
        address_line_1: value.address_line_1.trim(),
        address_line_2: value.address_line_2.trim(),
        city: value.city.trim(),
        state: value.state.trim(),
        country: value.country.trim(),
        zip: value.zip.trim(),
        is_active: !!value.is_active,
      },
    }));
  }

  protected requestCredentials(organization: Organization): void {
    this.store.dispatch(OrganizationGenerateCredentialsRequest({ id: organization.id }));
  }

  protected confirmToggleActive(organization: Organization): void {
    const nextState = !organization.is_active;
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '420px',
      data: {
        title: nextState ? 'Enable Organization' : 'Disable Organization',
        content: nextState
          ? `Enable ${organization.name} so its users can access ${this.brand.name} again?`
          : `Disable ${organization.name}? Users in this organization will lose access.`,
        confirmButton: nextState ? 'Enable' : 'Disable',
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.store.dispatch(OrganizationSetActiveRequest({ id: organization.id, isActive: nextState }));
    });
  }

  protected clearGeneratedCredentials(): void {
    this.generatedCredentials.set(null);
  }
}
